import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React, { useContext, useEffect, useState } from 'react';
import { UserManagementActionType } from '../Types/UserManagementActionType';
import { UserManagementDispatch, UserManagementState } from '../UserManagement';
import { Role } from '../../../../shared-types/Roles';
import { IRetentionStatus } from '../../../../shared-interfaces/User/IRetentionStatus';
import { IUserManagementFilter } from '../Interfaces/IUserManagementFilter';
import useToggle from '../../../../hooks/useToggle';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';
import LaunchDarklyContext from '../../../../shared-components/contexts/LaunchDarklyContext';

interface IRoleTypes {
  value: Role;
  label: string;
}

const useStyles = makeStyles({
  formElement: {
    width: '100%',
    marginTop: '10px',
  },
  paper: {
    padding: '10px 15px',
  },
});

const UserManagementFilterPanel = () => {
  const launchDarkly = useContext(LaunchDarklyContext);
  const classes = useStyles();

  const state = useContext(UserManagementState);
  const dispatch = useContext(UserManagementDispatch);
  const userAttributeContext = useContext(UserAttributesContext);

  const [userRetentionStatuses, setUserRetentionStatuses] = useState<number[]>(
    userAttributeContext.retentionStatuses.map(x => x.id),
  );

  const [selectAllRoles, setSelectAllRoles] = useToggle(true);

  const [selectAllRetentionStatuses, setSelectAllRetentionsStatuses] =
    useToggle(true);

  const roles = [
    { value: Role.Administrator, label: 'Administrator' },
    { value: Role.Advisor, label: 'Advisor' },
    { value: Role.Executive, label: 'Executive' },
    { value: Role.Generalist, label: 'Generalist' },
    { value: Role.PeerAdvisor, label: 'Peer Advisor' },
    { value: Role.Student, label: 'Student' },
  ] as IRoleTypes[];
  const [role, setRole] = useState<Role[]>([
    Role.Administrator,
    Role.Advisor,
    Role.Executive,
    Role.Generalist,
    Role.PeerAdvisor,
    Role.Student,
  ]);

  function formatFilters(): IUserManagementFilter {
    return {
      roles: role,
      retentionStatus: userRetentionStatuses,
      search: state.filter.search,
    };
  }

  function resetFilters() {
    setRole([
      Role.Administrator,
      Role.Advisor,
      Role.Executive,
      Role.Generalist,
      Role.PeerAdvisor,
      Role.Student,
    ]);
    setUserRetentionStatuses(
      userAttributeContext.retentionStatuses.map(x => x.id),
    );
    dispatch({
      type: UserManagementActionType.Reset,
    });
  }

  function onRoleSelectAll() {
    if (!selectAllRoles) {
      setRole([
        Role.Administrator,
        Role.Advisor,
        Role.Executive,
        Role.Generalist,
        Role.PeerAdvisor,
        Role.Student,
      ]);
    } else {
      setRole([]);
    }
    setSelectAllRoles();
  }

  function onRoleSelect(type: Role[]) {
    const filteredTypes = type.filter(s => s !== undefined);

    setRole(filteredTypes);
    if (filteredTypes.length === 0) {
      setSelectAllRoles(false);
      setRole([]);
    } else if (filteredTypes.length >= 6) {
      setSelectAllRoles(true);
      setRole(filteredTypes);
    }
  }

  function onRetentionStatusesSelectAll() {
    if (!selectAllRetentionStatuses) {
      setUserRetentionStatuses(
        userAttributeContext.retentionStatuses.map(x => x.id),
      );
    } else {
      setUserRetentionStatuses([]);
    }
    setSelectAllRetentionsStatuses();
  }

  function onRetentionStatusSelect(type: number[]) {
    const filteredTypes = type.filter(s => s !== undefined);

    setUserRetentionStatuses(filteredTypes);
    if (filteredTypes.length === 0) {
      setSelectAllRetentionsStatuses(false);
      setUserRetentionStatuses([]);
    } else if (
      filteredTypes.length >= userAttributeContext.retentionStatuses.length
    ) {
      setSelectAllRetentionsStatuses(true);
      setUserRetentionStatuses(filteredTypes);
    }
  }

  return (
    <Paper elevation={2} square className={classes.paper}>
      <Box>
        <FormControl variant="outlined" className={classes.formElement}>
          <TextField
            variant="outlined"
            value={state.filter.search}
            onChange={x =>
              dispatch({
                type: UserManagementActionType.SetSearchFilter,
                value: x.target.value,
              })
            }
            label="Search Users"
            InputLabelProps={{ id: 'search-users' }}
            inputProps={{ 'aria-labelledby': 'search-users' }}
            InputProps={{
              startAdornment: !state.filter.search && <SearchIcon />,
              endAdornment: state.filter.search && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      dispatch({
                        type: UserManagementActionType.SetSearchFilter,
                        value: '',
                      });
                    }}
                    size="large"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <FormControl variant="outlined" className={classes.formElement}>
          <InputLabel id="role-mutiple-checkbox-label">Role</InputLabel>
          <Select
            variant="outlined"
            labelId="role-mutiple-checkbox-label"
            label="Role"
            id="role-mutiple-checkbox"
            multiple
            value={role}
            inputProps={{ 'aria-labelledby': 'role-mutiple-checkbox-label' }}
            renderValue={selected => {
              if (role.length === 0 || role.length === 6) {
                return 'All Roles';
              } else {
                return (selected as Role[])
                  .sort((a, b) => a - b)
                  .map(x => roles.find(y => y.value === x)?.label)
                  .join(', ');
              }
            }}
            onChange={e => {
              onRoleSelect(e.target.value as Role[]);
            }}
          >
            {launchDarkly['filters-select-deselect-all'] && (
              <MenuItem
                style={{
                  borderBottom: '2px solid #dcdcdc',
                  backgroundColor: '#fff',
                }}
                onClick={(e: any) => {
                  onRoleSelectAll();
                  e.stopImmediatePropigation();
                }}
                id="select-all-checkbox"
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    checked={selectAllRoles}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${selectAllRoles ? 'Deselect' : 'Select'} All`}
                />
              </MenuItem>
            )}
            {roles.map(x => (
              <MenuItem
                key={x.label}
                value={x.value}
                style={{ backgroundColor: '#fff' }}
              >
                <Checkbox
                  color="primary"
                  checked={role.findIndex(y => y === x.value) !== -1}
                />
                <ListItemText primary={x.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" className={classes.formElement}>
          <InputLabel id="retentionstatus-mutiple-checkbox-label">
            Retention Status
          </InputLabel>
          <Select
            variant="outlined"
            labelId="retentionstatus-mutiple-checkbox-label"
            label="Retention Status"
            id="retentionstatus-mutiple-checkbox"
            inputProps={{
              'aria-labelledby': 'retentionstatus-mutiple-checkbox-label',
            }}
            multiple
            value={userRetentionStatuses}
            renderValue={selected => {
              if (
                userRetentionStatuses.length === 0 ||
                userRetentionStatuses.length === 4
              ) {
                return 'All Retention Statuses';
              } else {
                return (selected as number[])
                  .map(
                    x =>
                      userAttributeContext.retentionStatuses.find(
                        y => y.id === x,
                      )?.name,
                  )
                  .join(', ');
              }
            }}
            onChange={e => onRetentionStatusSelect(e.target.value as number[])}
          >
            {' '}
            {launchDarkly['filters-select-deselect-all'] && (
              <MenuItem
                style={{
                  borderBottom: '2px solid #dcdcdc',
                  backgroundColor: '#fff',
                }}
                onClick={(e: any) => {
                  onRetentionStatusesSelectAll();
                  e.stopImmediatePropigation();
                }}
                id="select-all-checkbox"
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    checked={selectAllRetentionStatuses}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${
                    selectAllRetentionStatuses ? 'Deselect' : 'Select'
                  } All`}
                />
              </MenuItem>
            )}
            {userAttributeContext.retentionStatuses.map(x => (
              <MenuItem
                key={x.id}
                value={x.id}
                style={{ backgroundColor: '#fff' }}
              >
                <Checkbox
                  color="primary"
                  checked={
                    userRetentionStatuses.findIndex(y => y === x.id) !== -1
                  }
                />
                <ListItemText primary={x.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" style={{ marginTop: '10px' }}>
        <Box flexGrow={1}>
          <Button
            variant="outlined"
            onClick={() => {
              resetFilters();
            }}
          >
            Reset Filters
          </Button>
        </Box>
        <Box>
          <Button
            color="primary"
            style={{ width: '110px' }}
            onClick={() => {
              dispatch({
                type: UserManagementActionType.SetUserManagementFilters,
                value: formatFilters(),
              });
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserManagementFilterPanel;
